import VStack from "../../../ui_atoms/VStack";
import { useTheme } from "../../../hooks/use-theme";
import { useI18n } from "../../../hooks/i18n-hooks";
import { TitleMediumText } from "../../FormattedText";
import FullWidthLine from "../../FullWidthLine";
import ListItem from "../../../ui_atoms/ListItem";
import { View } from "react-native";
import SelectField from "../../SelectField";
import Text from "../../Text";
import {
    usePayrollBreakdownExtras,
    UsePayrollBreakdownExtrasProps,
} from "../../../hooks/use-payroll-breakdown-extras";
import { Control, useFieldArray } from "react-hook-form";
import { FormData, PayrollBreakdownEditorProps } from "./PayrollBreakdownEditor";
import { useEffect, useMemo } from "react";
import { EarningType, SalaryDeductionType } from "../../../__generated__/graphql";
import { MissionPayrollBreakdownFieldsFragment } from "../../../__generated__/operations/graphql";

const CONTENT_LINE_HEIGHT = 55;

export type ExtrasSummaryRowFunctions = {
    onEdit: () => void;
    onDelete: () => void;
};

export type PayrollBreakdownExtrasProps = {
    control: Control<FormData>;
    clientDeductions?: SalaryDeductionType[];
    payrollBreakdown: MissionPayrollBreakdownFieldsFragment;
    canManageAbsences?: boolean;
    overtime?: UsePayrollBreakdownExtrasProps["overtime"] & {
        overtimeWorkDelta: number;
        remove: () => void;
    };
    setCustomSummaryRows: PayrollBreakdownEditorProps["setCustomSummaryRows"];
};

const PayrollBreakdownExtras = ({
    control,
    clientDeductions,
    canManageAbsences,
    payrollBreakdown,
    overtime,
    setCustomSummaryRows,
}: PayrollBreakdownExtrasProps) => {
    const i18n = useI18n();
    const { colors, marginSize, fonts } = useTheme();

    const {
        fields: bonusFields,
        append: appendBonus,
        remove: removeBonusAtIndex,
        update: updateBonus,
    } = useFieldArray({
        name: "bonuses",
        control,
    });

    const {
        fields: absenceFields,
        append: appendAbsence,
        remove: removeAbsenceAtIndex,
        update: updateAbsence,
    } = useFieldArray({
        name: "absences",
        control,
    });

    const {
        fields: effectiveFeeFields,
        append: appendEffectiveFee,
        remove: removeEffectiveFeeAtIndex,
        update: updateEffectiveFee,
    } = useFieldArray({
        name: "effectiveFees",
        control,
    });

    const {
        fields: auxiliarySalaryFields,
        append: appendAuxiliarySalary,
        remove: removeAuxiliarySalaryAtIndex,
        update: updateAuxiliarySalary,
    } = useFieldArray({
        name: "auxiliarySalaries",
        control,
    });

    // Server-side rule: auxiliary salaries only exist on hourly missions without
    // overtime compensation.
    const canManageAuxiliarySalaries =
        payrollBreakdown.missionInfo.earningType === EarningType.Hourly &&
        !payrollBreakdown.missionInfo.compensateOvertime;

    const payrollBreakdownExtrasProps = useMemo<UsePayrollBreakdownExtrasProps>(
        () => ({
            bonus: {
                append: appendBonus,
                update: updateBonus,
                remove: removeBonusAtIndex,
                clientDeductions: clientDeductions || [],
                arrayLength: bonusFields.length,
            },
            effectiveFees: {
                append: appendEffectiveFee,
                update: updateEffectiveFee,
                remove: removeEffectiveFeeAtIndex,
                arrayLength: effectiveFeeFields.length,
            },
            ...(canManageAuxiliarySalaries
                ? {
                      auxiliarySalaries: {
                          append: appendAuxiliarySalary,
                          update: updateAuxiliarySalary,
                          remove: removeAuxiliarySalaryAtIndex,
                          arrayLength: auxiliarySalaryFields.length,
                      },
                  }
                : {}),
            ...(canManageAbsences
                ? {
                      absences: {
                          append: appendAbsence,
                          update: updateAbsence,
                          remove: removeAbsenceAtIndex,
                          minDate: payrollBreakdown.startDate
                              ? new Date(payrollBreakdown.startDate)
                              : undefined,
                          maxDate: payrollBreakdown.endDate
                              ? new Date(payrollBreakdown.endDate)
                              : undefined,
                          arrayLength: absenceFields.length,
                      },
                  }
                : {}),
            overtime,
        }),
        [
            appendBonus,
            updateBonus,
            removeBonusAtIndex,
            clientDeductions,
            bonusFields.length,
            appendEffectiveFee,
            updateEffectiveFee,
            removeEffectiveFeeAtIndex,
            effectiveFeeFields.length,
            canManageAuxiliarySalaries,
            appendAuxiliarySalary,
            updateAuxiliarySalary,
            removeAuxiliarySalaryAtIndex,
            auxiliarySalaryFields.length,
            canManageAbsences,
            appendAbsence,
            updateAbsence,
            removeAbsenceAtIndex,
            payrollBreakdown.startDate,
            payrollBreakdown.endDate,
            absenceFields.length,
            overtime,
        ],
    );

    const {
        openBonusModal,
        bonusCreateModal,
        openEffectiveFeesModal,
        effectiveFeesModal,
        openAuxiliarySalaryModal,
        auxiliarySalaryModal,
        openAbsenceModal,
        absencesModal,
        openOvertimeModal,
        overtimeModal,
    } = usePayrollBreakdownExtras(payrollBreakdownExtrasProps);

    useEffect(() => {
        setCustomSummaryRows([
            ...bonusFields.map((b, index) => {
                return {
                    category: "bonus",
                    productKey: "salary_bonus",
                    index,
                    onEdit: () => {
                        openBonusModal({ ...b, index });
                    },
                    onDelete: () => {
                        removeBonusAtIndex(index);
                    },
                };
            }),
            ...effectiveFeeFields.map((e, index) => {
                return {
                    category: "effective_fee",
                    productKey: "effective_fee",
                    index,
                    onEdit: () => {
                        openEffectiveFeesModal({ ...e, index });
                    },
                    onDelete: () => {
                        removeEffectiveFeeAtIndex(index);
                    },
                };
            }),
            ...(canManageAuxiliarySalaries
                ? auxiliarySalaryFields.map((a, index) => {
                      return {
                          category: "auxiliary_salary",
                          productKey: "salary_hourly",
                          index,
                          onEdit: () => {
                              openAuxiliarySalaryModal({ ...a, index });
                          },
                          onDelete: () => {
                              removeAuxiliarySalaryAtIndex(index);
                          },
                      };
                  })
                : []),
            ...(canManageAbsences
                ? absenceFields.map((a, index) => {
                      return {
                          category: "absence",
                          productKey: a.type,
                          index,
                          onEdit: () => {
                              openAbsenceModal({ ...a, index });
                          },
                          onDelete: () => {
                              removeAbsenceAtIndex(index);
                          },
                      };
                  })
                : []),
            ...(overtime
                ? [
                      {
                          category: "salary",
                          productKey: "salary_overtime_0_from_balance",
                          onEdit: () => {
                              openOvertimeModal(overtime.overtimeWorkDelta);
                          },
                          onDelete: overtime.remove,
                      },
                  ]
                : []),
        ]);
    }, [
        absenceFields,
        bonusFields,
        effectiveFeeFields,
        auxiliarySalaryFields,
        openAbsenceModal,
        openBonusModal,
        openEffectiveFeesModal,
        openAuxiliarySalaryModal,
        payrollBreakdown,
        payrollBreakdown.absences,
        removeAbsenceAtIndex,
        removeBonusAtIndex,
        removeEffectiveFeeAtIndex,
        removeAuxiliarySalaryAtIndex,
        setCustomSummaryRows,
        overtime,
        openOvertimeModal,
        canManageAbsences,
        canManageAuxiliarySalaries,
    ]);

    return (
        <>
            {bonusCreateModal}
            {effectiveFeesModal}
            {auxiliarySalaryModal}
            {absencesModal}
            {overtimeModal}
            <VStack>
                <FullWidthLine />
                <View style={{ height: CONTENT_LINE_HEIGHT, justifyContent: "center" }}>
                    <TitleMediumText>{i18n.t("payroll_breakdown.extras")}</TitleMediumText>
                </View>
                <>
                    {payrollBreakdownExtrasProps.absences && (
                        <>
                            <FullWidthLine />
                            <ListItem
                                style={{
                                    height: CONTENT_LINE_HEIGHT,
                                    marginHorizontal: -marginSize,
                                    borderBottomWidth: 0,
                                }}
                                labels={[
                                    {
                                        mode: "cta",
                                        text: i18n.t("payroll_breakdown.enter_absence"),
                                    },
                                ]}
                                rightIcon="add"
                                rightActionMode="medium"
                                rightIconColor={colors.secondary}
                                onPress={() => openAbsenceModal()}
                            />
                        </>
                    )}
                    {(payrollBreakdownExtrasProps.bonus ||
                        payrollBreakdownExtrasProps.effectiveFees ||
                        overtime) && (
                        <>
                            <FullWidthLine />
                            <SelectField
                                labelLeftPadding={0}
                                right={
                                    <Text
                                        style={{
                                            ...fonts.medium,
                                            color: colors.secondary,
                                        }}
                                    >
                                        ...
                                    </Text>
                                }
                                dismissOnPress
                                style={{
                                    height: CONTENT_LINE_HEIGHT,
                                    borderBottomWidth: 0,
                                }}
                                asListAction="primary"
                                label={i18n.t("payroll_breakdown.refund_or_pay")}
                                data={[
                                    ...(payrollBreakdownExtrasProps.bonus
                                        ? [
                                              {
                                                  label: "",
                                                  value: "",
                                                  rightActionMode: "none" as const,
                                                  description: i18n.t(
                                                      "payroll_breakdown.pay_bonus",
                                                  ),
                                                  globalTextColor: colors.secondary,
                                                  onPress: openBonusModal,
                                              },
                                          ]
                                        : []),
                                    ...(payrollBreakdownExtrasProps.auxiliarySalaries
                                        ? [
                                              {
                                                  label: "",
                                                  value: "",
                                                  globalTextColor: colors.secondary,
                                                  description: i18n.t(
                                                      "payroll_breakdown.pay_auxiliary_salary",
                                                  ),
                                                  rightActionMode: "none" as const,
                                                  labels: [
                                                      {
                                                          mode: "miniCta" as const,
                                                          color: colors.greyAccessible,
                                                          text: i18n.t(
                                                              "payroll_breakdown.pay_auxiliary_salary_description",
                                                          ),
                                                      },
                                                  ],
                                                  onPress: openAuxiliarySalaryModal,
                                              },
                                          ]
                                        : []),
                                    ...(payrollBreakdownExtrasProps.effectiveFees
                                        ? [
                                              {
                                                  label: "",
                                                  value: "",
                                                  globalTextColor: colors.secondary,
                                                  description: i18n.t(
                                                      "payroll_breakdown.refund_effective_fees",
                                                  ),
                                                  rightActionMode: "none" as const,
                                                  labels: [
                                                      {
                                                          mode: "miniCta" as const,
                                                          color: colors.greyAccessible,
                                                          text: i18n.t(
                                                              "payroll_breakdown.refund_effective_fees_description",
                                                          ),
                                                      },
                                                  ],
                                                  onPress: openEffectiveFeesModal,
                                              },
                                          ]
                                        : []),
                                    ...((overtime?.overtimeWorkDelta || 0) > 0
                                        ? [
                                              {
                                                  label: "",
                                                  value: "",
                                                  rightActionMode: "none" as const,
                                                  description: i18n.t(
                                                      "payroll_breakdown.pay_overtime_work",
                                                      {
                                                          overtimeDeltaHours:
                                                              overtime?.overtimeWorkDelta,
                                                      },
                                                  ),
                                                  globalTextColor: colors.secondary,
                                                  onPress: openOvertimeModal,
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        </>
                    )}
                    <FullWidthLine />
                </>
            </VStack>
        </>
    );
};

export default PayrollBreakdownExtras;
